import { useState, useCallback, useEffect, type FormEvent } from 'react';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/contexts/AuthContext';
import { useApiQuery } from '@/hooks/useApiQuery';

interface Company { id: number; name: string; }

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  role: string;
  role_id: number;
  is_active: number;
  company_id: number | null;
}

interface EditUserModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: User | null;
}

const ROLES = [
  { id: 1, name: 'superadmin', label: 'Super Admin' },
  { id: 2, name: 'company_admin', label: 'Company Admin' },
  { id: 3, name: 'kyc_officer', label: 'KYC Officer' },
  { id: 4, name: 'customer', label: 'Customer' },
];

export default function EditUserModal({ open, onClose, onSuccess, user }: EditUserModalProps) {
  const { user: currentUser } = useAuth();
  const { put } = useApi();
  const isSuperAdmin = currentUser?.role === 'superadmin';

  const { data: companiesData } = useApiQuery<{ companies: Company[] }>({
    endpoint: '/companies',
    enabled: isSuperAdmin && open,
  });

  const companies = companiesData?.companies || [];

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    roleId: 2,
    isActive: true,
    companyId: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user && open) {
      setForm({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        phone: user.phone || '',
        roleId: user.role_id || ROLES.find(r => r.name === user.role)?.id || 2,
        isActive: !!user.is_active,
        companyId: user.company_id ? String(user.company_id) : '',
      });
      setError(null);
      setLoading(false);
    }
  }, [user, open]);

  const updateField = useCallback((field: string, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleClose = useCallback(() => {
    setError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setError(null);
    setLoading(true);

    try {
      const body: Record<string, unknown> = {};
      if (form.firstName.trim()) body.firstName = form.firstName.trim();
      if (form.lastName.trim()) body.lastName = form.lastName.trim();
      body.phone = form.phone.trim() || null;
      body.isActive = form.isActive;
      if (isSuperAdmin) {
        body.roleId = Number(form.roleId);
        body.companyId = form.companyId ? Number(form.companyId) : null;
      }

      await put(`/users/${user.id}`, body);
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
      setLoading(false);
    }
  }, [form, isSuperAdmin, put, user, onSuccess, handleClose]);

  if (!open || !user) return null;

  const companyName = companies.find(c => c.id === user.company_id)?.name;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
          <h2 className="text-lg font-bold text-stone-900">Edit User</h2>
          <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}

          <div className="mb-2">
            <p className="text-stone-400 text-xs uppercase tracking-wider">Email</p>
            <p className="text-stone-900 text-sm font-medium">{user.email}</p>
          </div>
          <div className="mb-2">
            <p className="text-stone-400 text-xs uppercase tracking-wider">Company</p>
            <p className="text-stone-900 text-sm font-medium">{companyName || `ID: ${user.company_id || '-'}`}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">First Name</label>
              <input
                required type="text" value={form.firstName}
                onChange={(e) => updateField('firstName', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">Last Name</label>
              <input
                required type="text" value={form.lastName}
                onChange={(e) => updateField('lastName', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">Phone</label>
              <input
                type="tel" value={form.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-500 mb-1">Status</label>
              <select
                value={form.isActive ? 'active' : 'inactive'}
                onChange={(e) => updateField('isActive', e.target.value === 'active')}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {isSuperAdmin && (
            <>
              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Role</label>
                <select
                  value={form.roleId}
                  onChange={(e) => updateField('roleId', Number(e.target.value))}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                >
                  {ROLES.map((r) => (
                    <option key={r.id} value={r.id}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-stone-500 mb-1">Company Assignment</label>
                <select
                  value={form.companyId}
                  onChange={(e) => updateField('companyId', e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 bg-white"
                >
                  <option value="">No company (unassigned)</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <p className="text-stone-400 text-xs mt-1">Changing company moves the user to a different tenant.</p>
              </div>
            </>
          )}

          <div className="pt-2 flex items-center justify-end gap-3">
            <button type="button" onClick={handleClose}
              className="px-4 py-2.5 rounded-full text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors whitespace-nowrap">
              Cancel
            </button>
            <button type="submit" disabled={loading}
              className="bg-brand-600 hover:bg-brand-700 disabled:bg-stone-300 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors flex items-center gap-2 whitespace-nowrap">
              {loading ? <><i className="ri-loader-4-line animate-spin" /> Saving...</> : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
